import React from 'react';
import { useTranslation } from 'react-i18next';
import { Questionnaire } from '../../types/fhir';
import Btn from '../Btn/Btn';
import Modal from '../Modal/Modal';
import SpinnerBox from '../Spinner/SpinnerBox';
import TherapyMaterialRenderer from '../TherapyMaterial/TherapyMaterialRenderer';
import VisibilityCheckbox from '../VisibilityCheckbox/VisibilityCheckbox';
import './TherapyMaterialPreviewModal.css';

type Props = {
    questionnaire: Questionnaire | null;
    isLoading: boolean;
    error: string | null;
    onClose: () => void;
    onUpload?: () => void;
    isUploading?: boolean;
    uploadError?: string | null;
    /** Shows the visibility choice for new therapy material. */
    isPrivate?: boolean;
    onPrivateChange?: (isPrivate: boolean) => void;
    /** Name of the existing therapy material the upload replaces, when editing. */
    replaceTargetName?: string;
};

const TherapyMaterialPreviewModal = ({
    questionnaire,
    isLoading,
    error,
    onClose,
    onUpload,
    isUploading = false,
    uploadError,
    isPrivate = false,
    onPrivateChange,
    replaceTargetName,
}: Props): JSX.Element => {
    const { t } = useTranslation();

    return (
        <Modal title={t('Therapy Material Preview')} close={onClose} size="large">
            <div className="therapy-material-preview">
                {onUpload && (
                    <p className="therapy-material-preview__verification">
                        {replaceTargetName
                            ? t(
                                  'Verify the therapy material below. Saving replaces "{{name}}" for everyone who can see it.',
                                  { name: replaceTargetName },
                              )
                            : t('Verify the therapy material below before uploading.')}
                    </p>
                )}
                {isLoading && (
                    <div className="therapy-material-preview__status">
                        <SpinnerBox />
                        <p>{t('Loading therapy material...')}</p>
                    </div>
                )}
                {error && !isLoading && (
                    <div className="therapy-material-preview__error">
                        {t('Failed to load therapy material')}: {error}
                    </div>
                )}
                {questionnaire && !isLoading && !error && <TherapyMaterialRenderer questionnaire={questionnaire} />}
                {uploadError && <div className="therapy-material-preview__error">{uploadError}</div>}
                {onUpload && questionnaire && !isLoading && !error && (
                    <>
                        {onPrivateChange && !replaceTargetName && (
                            <VisibilityCheckbox
                                isPrivate={isPrivate}
                                onChange={onPrivateChange}
                                disabled={isUploading}
                            />
                        )}
                        <div className="therapy-material-preview__actions">
                            <Btn title={t('Cancel')} variant="secondary" onClick={onClose} disabled={isUploading} />
                            <Btn
                                title={
                                    replaceTargetName
                                        ? isUploading
                                            ? t('Saving changes...')
                                            : t('Save changes')
                                        : isUploading
                                        ? t('Uploading therapy material...')
                                        : t('Upload Therapy Material')
                                }
                                onClick={onUpload}
                                disabled={isUploading}
                            />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default TherapyMaterialPreviewModal;
